import React, { useEffect, useState, useCallback, useMemo } from "react"
import Moment from "react-moment"
import { getFieldData } from "../../../Services/appService"
import { useDispatch, useSelector } from "react-redux";
import { getCurrentUser } from "../../store/auth";
import {
  useLoadCandidateAppsQuery,
  useLoadAppRuningTasksQuery,
  useLoadAppFormLibraryQuery,
  useLoadAppDocumentsQuery,
} from "../../store/candidateApiSlice";
import { loadAppFormLibrary, loadAppDocuments, loadAppRuningTasks } from "../../store/candidate";
import AddEditNotes from "../../../Components/ApplicantProcessing/AddEditNotes";
import { setCurrentPageTitle, setCurrentRoute } from "../../store/layoutUI";
import UploadDocPopup from "../../../Components/UploadDocPopup";
import ViewFormModal from "../ViewFormModal";
import useDocDownload from "../../hooks/useDocDownload";
import { allowFormLibraryEdit, getFormLibraryEditStatus } from "../../store/forms";
import { getComponentUserStatus, UpdateComponentsUserStatus } from "../../store/tools";
import { OverlayTrigger, Popover } from "react-bootstrap";
import { getAlert, getConfirmAlert } from "../../../Services/appAlerts";
import { achDocumentDeleted, deleteAchDocument, deleteDocument, getAchDocumentDeleteStatus, getDocumentDeleteStatus } from "../../store/documents";

const ACH_STYLES = {
  shell: {
    fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    display: "flex",
    flexDirection: "column",
    gap: "20px",
    width: "100%"
  },
  hero: {
    borderRadius: "0px",
    padding: "14px 20px",
    border: "1px solid rgba(219, 234, 254, 0.95)",
    background: "#ffffff",
    boxShadow: "none"
  },
  heroRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "18px",
    flexWrap: "wrap"
  },
  heroCopy: {
    minWidth: "260px",
    flex: "1 1 480px"
  },
  heroTitleRow: {
    display: "flex",
    alignItems: "center",
    gap: "14px"
  },
  heroIcon: {
    width: "32px",
    height: "32px",
    borderRadius: "0px",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    background: "#ffffff",
    color: "#000000",
    fontSize: "18px",
    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.05)",
    flexShrink: 0
  },
  heroTitle: {
    fontSize: "22px",
    fontWeight: 600,
    lineHeight: 1.2,
    color: "#0f172a",
    margin: "0 0 3px 0",
    letterSpacing: "-0.01em"
  },
  heroText: {
    fontSize: "13px",
    lineHeight: 1.5,
    color: "#475569",
    margin: 0,
    maxWidth: "760px"
  },
  heroAction: {
    display: "inline-flex",
    alignItems: "center",
    gap: "8px",
    borderRadius: "0px",
    border: "1px solid #2563eb",
    background: "#2563eb",
    color: "#ffffff",
    padding: "9px 18px",
    fontSize: "13px",
    fontWeight: 600,
    boxShadow: "none",
    cursor: "pointer",
    transition: "all 0.2s ease"
  },
  statGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
    gap: "14px",
    marginTop: "20px"
  },
  statCard: {
    display: "flex",
    alignItems: "flex-start",
    gap: "12px",
    backgroundColor: "#ffffff",
    border: "1px solid #e5e5e5",
    borderRadius: "0px",
    padding: "16px",
    boxShadow: "none",
    transition: "all 0.2s ease"
  },
  statIcon: {
    width: "32px",
    height: "32px",
    borderRadius: "0px",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    background: "#ffffff",
    color: "#000000",
    fontSize: "14px",
    flexShrink: 0
  },
  statContent: {
    minWidth: 0
  },
  statLabel: {
    fontSize: "11px",
    fontWeight: 600,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    color: "#64748b",
    marginBottom: "4px"
  },
  statValue: {
    fontSize: "24px",
    fontWeight: 600,
    lineHeight: 1,
    color: "#0f172a"
  },
  panel: {
    backgroundColor: "#ffffff",
    border: "1px solid #e5e5e5",
    borderRadius: "0px",
    boxShadow: "none",
    overflow: "hidden"
  },
  cardHeader: {
    padding: "14px 20px",
    borderBottom: "1px solid #e5e5e5",
    background: "#ffffff"
  },
  headerRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "16px",
    flexWrap: "wrap"
  },
  titleRow: {
    display: "flex",
    alignItems: "center",
    gap: "12px"
  },
  titleIcon: {
    width: "32px",
    height: "32px",
    borderRadius: "0px",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    background: "#ffffff",
    color: "#000000",
    fontSize: "14px",
    border: "1px solid #e5e5e5",
    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.05)",
    flexShrink: 0
  },
  panelTitle: {
    fontSize: "16px",
    fontWeight: 600,
    color: "#000000",
    margin: "0 0 2px 0"
  },
  panelText: {
    fontSize: "13px",
    color: "#666666",
    margin: 0
  },
  cardBody: {
    padding: 0
  },
  utilityRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "12px",
    flexWrap: "wrap",
    padding: "14px 18px"
  },
  refreshButton: {
      width: "fit-content",
      boxSizing: "border-box",
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      gap: "8px",
      borderRadius: "0px",
      border: "1px solid #e5e5e5",
      background: "#ffffff",
      color: "#333333",
      padding: "8px 16px",
      fontSize: "13px",
      fontWeight: 600,
      boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
      cursor: "pointer",
      transition: "all 0.2s ease"
    },
  tableWrap: {
    overflowX: "auto",
    backgroundColor: "#ffffff"
  },
  table: {
    marginBottom: 0,
    width: "100%",
    minWidth: "860px",
    borderCollapse: "separate",
    borderSpacing: 0
  },
  tableHeadCell: {
    borderTop: "none",
    fontSize: "11px",
    fontWeight: 600,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    color: "#64748b",
    background: "#f8fafc",
    borderBottom: "1px solid #e5e5e5",
    padding: "14px 18px"
  },
  cell: {
    verticalAlign: "top",
    padding: "16px 18px",
    borderBottom: "1px solid rgba(241, 245, 249, 1)",
    background: "#ffffff"
  },
  achInfoContainer: {
    display: "flex",
    flexDirection: "column",
    gap: "6px"
  },
  achText: {
    fontSize: "13.5px",
    lineHeight: 1.4,
    color: "#1e293b",
    display: "flex",
    alignItems: "center",
    gap: "8px"
  },
  achLabel: {
    fontSize: "11px",
    fontWeight: 600,
    color: "#64748b",
    textTransform: "uppercase",
    letterSpacing: "0.02em",
    minWidth: "110px"
  },
  achIcon: {
    width: "24px",
    textAlign: "center",
    color: "#2563eb",
    fontSize: "13px"
  },
  statusContainer: {
    display: "flex",
    alignItems: "center"
  },
  completePill: {
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    padding: "5px 12px",
    borderRadius: "0px",
    fontSize: "12px",
    fontWeight: 600,
    border: "1px solid #2dce89",
    backgroundColor: "#2dce898c",
    color: "#0f172a"
  },
  pendingPill: {
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    padding: "5px 12px",
    borderRadius: "0px",
    fontSize: "12px",
    fontWeight: 600,
    border: "1px solid #ffd600",
    backgroundColor: "#ffd600",
    color: "#0f172a"
  },
  docCard: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
    padding: "10px 12px",
    borderRadius: "0px",
    background: "#ffffff",
    border: "1px solid #e2e8f0",
    boxShadow: "none",
    width: "100%",
    maxWidth: "260px",
    transition: "all 0.2s ease"
  },
  docHeaderRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "8px"
  },
  docTitleGroup: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    minWidth: 0,
    flex: 1
  },
  docBadgeIcon: {
    width: "30px",
    height: "30px",
    borderRadius: "0px",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    background: "#ffffff",
    border: "1px solid #e2e8f0",
    color: "#475569",
    fontSize: "13px",
    boxShadow: "none",
    flexShrink: 0
  },
  docNameBtn: {
    border: "none",
    background: "transparent",
    color: "#2563eb",
    fontSize: "13px",
    fontWeight: 600,
    textAlign: "left",
    padding: 0,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    cursor: "pointer"
  },
  docDeleteBtn: {
      boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
    width: "28px",
    height: "28px",
    borderRadius: "0px",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    background: "#fef2f2",
    color: "#dc2626",
    border: "1px solid rgba(254, 202, 202, 0.9)",
    fontSize: "12px",
    cursor: "pointer",
    flexShrink: 0,
    transition: "all 0.2s ease"
  },
  docNotesBtn: {
      boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
    width: "28px",
    height: "28px",
    borderRadius: "0px",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    background: "#f8fafc",
    color: "#475569",
    border: "1px solid #e2e8f0",
    fontSize: "12px",
    cursor: "pointer",
    flexShrink: 0,
    transition: "all 0.2s ease"
  },
  primaryAction: {
    display: "inline-flex",
    alignItems: "center",
    gap: "8px",
    borderRadius: "0px",
    border: "1px solid #cbd5e1",
    background: "#ffffff",
    color: "#334155",
    padding: "9px 18px",
    fontSize: "13px",
    fontWeight: 600,
    boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
    cursor: "pointer",
    transition: "all 0.2s ease"
  },
  secondaryAction: {
    display: "inline-flex",
    alignItems: "center",
    gap: "8px",
    borderRadius: "0px",
    border: "1px solid #cbd5e1",
    background: "#ffffff",
    color: "#334155",
    padding: "9px 18px",
    fontSize: "13px",
    fontWeight: 600,
    boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
    cursor: "pointer",
    transition: "all 0.2s ease"
  },
  formCard: {
    minWidth: "220px",
    width: "100%",
    maxWidth: "285px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "10px",
    padding: "10px 12px",
    borderRadius: "0px",
    border: "1px solid rgba(219, 234, 254, 0.95)",
    background: "#ffffff",
    boxShadow: "none",
    transition: "all 0.2s ease"
  },
  formCardLeft: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    background: "transparent",
    border: "none",
    padding: 0,
    cursor: "pointer",
    textAlign: "left",
    flex: 1,
    minWidth: 0
  },
  reqEditBtn: {
    display: "inline-flex",
    alignItems: "center",
    gap: "5px",
    borderRadius: "0px",
    border: "1px solid rgba(191, 219, 254, 0.9)",
    backgroundColor: "#eff6ff",
    color: "#2563eb",
    padding: "6px 10px",
    fontSize: "11.5px",
    fontWeight: 600,
    cursor: "pointer",
    whiteSpace: "nowrap",
    flexShrink: 0,
    transition: "all 0.2s ease"
  },
  formActionButton: {
      width: "fit-content",
      boxSizing: "border-box",
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      gap: "8px",
      borderRadius: "0px",
      border: "1px solid #cbd5e1",
      background: "#ffffff",
      color: "#334155",
      padding: "9px 18px",
      fontSize: "13px",
      fontWeight: 600,
      boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
      cursor: "pointer",
      transition: "all 0.2s ease"
    },
  formActionIcon: {
    width: "36px",
    height: "36px",
    borderRadius: "0px",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#eff6ff",
    border: "1px solid rgba(191, 219, 254, 0.9)",
    color: "#2563eb",
    fontSize: "14px",
    boxShadow: "none",
    flexShrink: 0
  },
  formActionCopy: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    gap: "1px",
    lineHeight: 1.2
  },
  formActionText: {
    fontSize: "13.5px",
    fontWeight: 600,
    color: "#0f172a"
  },
  formActionHint: {
    fontSize: "11px",
    fontWeight: 600,
    color: "#64748b"
  },
  lastEditBadge: {
    width: "fit-content",
    boxSizing: "border-box",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "flex-start",
    gap: "6px",
    padding: "0",
    borderRadius: "0px",
    background: "transparent",
    border: "none",
    fontSize: "11.5px",
    fontWeight: 600,
    color: "#64748b"
  },
  requestEditButton: {
      width: "fit-content",
      boxSizing: "border-box",
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      gap: "8px",
      borderRadius: "0px",
      border: "1px solid #e5e5e5",
      background: "#ffffff",
      color: "#333333",
      padding: "8px 16px",
      fontSize: "13px",
      fontWeight: 600,
      boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
      cursor: "pointer",
      transition: "all 0.2s ease"
    },
  textButton: {
    border: "none",
    background: "transparent",
    color: "#2563eb",
    padding: 0,
    fontSize: "12px",
    fontWeight: 600
  },
  dangerButton: {
    border: "none",
    background: "transparent",
    color: "#dc2626",
    padding: 0,
    fontSize: "12px",
    fontWeight: 600
  },
  mutedText: {
    fontSize: "12px",
    color: "#64748b",
    lineHeight: 1.55
  },
  statusPill: {
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    padding: "4px 10px",
    borderRadius: "0px",
    fontSize: "11px",
    fontWeight: 600,
    border: "1px solid rgba(226, 232, 240, 1)",
    backgroundColor: "#f8fafc",
    color: "#475569"
  },
  lastEdited: {
    fontSize: "12px",
    color: "#64748b",
    display: "flex",
    alignItems: "center",
    gap: "5px",
    marginTop: "4px"
  },
  emptyState: {
    padding: "28px 20px",
    textAlign: "center",
    color: "#64748b",
    fontSize: "13px"
  }
};

export default function AchInfo() {

  const route = [
    { name: "Contract Management", url: "" },
    { name: "Enrollment Steps", url: "" },
  ];

  const currentUser = getCurrentUser();
  let usrId = currentUser?.id;
  if (currentUser?.linkUser) usrId = currentUser.linkUser;

  const { data: candidateAppsSelector = [] } = useLoadCandidateAppsQuery(usrId, { skip: !usrId });

  const formLibraryEditStatusSelector = useSelector(getFormLibraryEditStatus);
  const componentUserStatusSelector = useSelector(getComponentUserStatus);
  const achDocumentDeleteStatusSelector = useSelector(getAchDocumentDeleteStatus);

  const [formType, setFormType] = useState("ACH Information");
  const [formShowHide, setFormShowHide] = useState(false);
  const [formObj, setFormObj] = useState(null);
  const [noteShowHide, setNoteShowHide] = useState(false);
  const [notesType, setNotesType] = useState("formEditReq");
  const [selectedDocType, setSelectedDocType] = useState("");
  const [docUploadShowHide, setDocUploadShowHide] = useState(false);
  const [allowEdit, setAllowEdit] = useState(false);

  const workflow = useMemo(() => {
    if (candidateAppsSelector.length > 0) {
      const mainWorkflow = candidateAppsSelector.filter(x => x.workflowType === 1);
      if (mainWorkflow.length > 0) {
        return mainWorkflow[0];
      }
    }
    return null;
  }, [candidateAppsSelector]);

  const appTasksSelectorResult = useLoadAppRuningTasksQuery(
    { wfId: workflow?.oldWorkflowId, userId: usrId },
    { skip: !workflow?.oldWorkflowId || !usrId }
  );
  const appTasksSelector = appTasksSelectorResult.data || [];

  const appFormLibrarySelectorResult = useLoadAppFormLibraryQuery(
    { wfId: workflow?.oldWorkflowId, userId: usrId },
    { skip: !workflow?.oldWorkflowId || !usrId }
  );
  const appFormLibrarySelector = appFormLibrarySelectorResult.data || [];

  const appDocumentsSelectorResult = useLoadAppDocumentsQuery(
    { wfId: workflow?.oldWorkflowId, userId: usrId },
    { skip: !workflow?.oldWorkflowId || !usrId }
  );
  const appDocumentsSelector = appDocumentsSelectorResult.data || [];

  const appTasklist = useMemo(() => {
    if (appTasksSelector.length > 0) {
      return appTasksSelector.filter(x => x.taskType === 1 && x.wiid > 0);
    }
    return [];
  }, [appTasksSelector]);

  const wfTask = useMemo(() => {
    return appTasklist.length > 0 ? appTasklist[0] : null;
  }, [appTasklist]);

  const reviewerTask = useMemo(() => {
    if (appTasklist.length > 0) {
      const reviewTasks = appTasklist.filter(x => x.registrationType === "WP");
      if (reviewTasks.length > 0) return reviewTasks[reviewTasks.length - 1];
    }
    return null;
  }, [appTasklist]);

  const appFormLibrary = useMemo(() => {
    if (appFormLibrarySelector.length > 0) {
      return appFormLibrarySelector.filter(x => x.formType === formType);
    }
    return [];
  }, [appFormLibrarySelector, formType]);

  const sentDocs = useMemo(() => {
    if (appDocumentsSelector.length > 0) {
      return appDocumentsSelector.filter(
        (x) =>
          x.type === "send" &&
          x.uploadType !== "Invoice" &&
          x.docType !== "internal" &&
          x.docType !== "Messaging"
      );
    }
    return [];
  }, [appDocumentsSelector]);

  const [handleDownloadFile] = useDocDownload();

  const dispatch = useDispatch();

  const handleUpdateComponent = useCallback((compItems, ciid) => {
    const comp = componentUserStatusSelector.filter(x => x.compId === 20);
    let updCompItems = "";
    if (comp.length > 0) {
      updCompItems = comp[0].compItems;
    }
    const compObj = {
      compName: "ACH",
      performerId: currentUser.id,
      compItems: compItems,
      ciid: ciid,
      wiid: 0,
      iiid: 0,
      compId: 20
    }
    if (compItems !== "" && compItems !== updCompItems) {
      dispatch(UpdateComponentsUserStatus(compObj));
    }
  }, [componentUserStatusSelector, currentUser.id, dispatch]);

  useEffect(() => {
    dispatch(setCurrentPageTitle("STEP-3 : ACH Information"));
    dispatch(setCurrentRoute(route))
  }, [dispatch, route])

  useEffect(() => {
    if (appFormLibrary.length > 0) {
      let frmItems = "";
      appFormLibrary.map((x, key) => {
        if (key > 0) frmItems += ",";
        frmItems += x.id;
        return x;
      });

      handleUpdateComponent(frmItems, appFormLibrary[0].ciid);
    }
  }, [appFormLibrary, handleUpdateComponent]);

  const handleDeleteAchDoc = async (id) => {
    const result = await getConfirmAlert("Warning", "Are you sure you want to delete this Document?", "warning");
    if (result.isConfirmed) {
      dispatch(deleteAchDocument(id));
    }
  }

  useEffect(() => {
    if (achDocumentDeleteStatusSelector) {
      getAlert("", "Deleted Successfully", "success");
      dispatch(achDocumentDeleted(false));
      if (workflow?.oldWorkflowId > 0) {
        let usrId = currentUser.id;
        if (currentUser.linkUser)
          usrId = currentUser.linkUser;
        dispatch(loadAppFormLibrary(workflow.oldWorkflowId, usrId, { subscribe: false, forceRefetch: true }));
        dispatch(loadAppDocuments(workflow.oldWorkflowId, usrId, { subscribe: false, forceRefetch: true }));
      }
    }
  }, [achDocumentDeleteStatusSelector, workflow?.oldWorkflowId, currentUser?.id, currentUser?.linkUser, dispatch])

  const handleUploadDocModalShowHide = (type) => {
    if (!reviewerTask) {
      getAlert("Warning", "No Reviewer Found. Please Complete Your Onboarding/Enrollment Form.", "warning");
      return;
    }
    const acno = getFieldData(wfTask?.stateData, "achinfo", "Account_No");
    const rno = getFieldData(wfTask?.stateData, "achinfo", "Routing");
    if (rno === "" || acno === "") {
      getAlert("Warning", "Please complete the ACH Information form before uploading Voided Check.", "warning");
      return;
    }

    if (type === "save" || type === "") {
      setDocUploadShowHide(false);
      setSelectedDocType("");
      let usrId = currentUser.id;
      if (currentUser.linkUser)
        usrId = currentUser.linkUser;
      if (workflow?.oldWorkflowId)
        dispatch(loadAppDocuments(workflow.oldWorkflowId, usrId, { subscribe: false, forceRefetch: true }));
    } else {
      setDocUploadShowHide(true);
      setSelectedDocType(type);
    }
  };

  const handleFormShowHide = (frm) => {
    if (frm === "save") {
      setFormShowHide(false);
      setFormObj("");
      let usrId = currentUser.id;
      if (currentUser.linkUser)
        usrId = currentUser.linkUser;
      if (workflow?.oldWorkflowId > 0 && usrId > 0) {
        dispatch(loadAppRuningTasks(workflow.oldWorkflowId, usrId, { subscribe: false, forceRefetch: true }));
        dispatch(loadAppFormLibrary(workflow.oldWorkflowId, usrId, { subscribe: false, forceRefetch: true }));
      }
    } else {
      setFormShowHide(!formShowHide);
      setFormObj(frm);
      if (frm?.allowEdit === 1) setAllowEdit(true);
    }
  };

  const handleNotesComments = (type, obj) => {
    if (type === "save") {
      setFormObj(null);
      setNoteShowHide(false);
      let usrId = currentUser.id;
      if (currentUser.linkUser)
        usrId = currentUser.linkUser;
      if (workflow?.oldWorkflowId > 0)
        dispatch(loadAppFormLibrary(workflow.oldWorkflowId, usrId, { subscribe: false, forceRefetch: true }));
    } else {
      setFormObj(obj);
      setNoteShowHide(!noteShowHide);
    }
  };

  const handleFormUpdate = () => {
    setAllowEdit(false);
    if (formObj?.id > 0)
      dispatch(allowFormLibraryEdit(formObj.id, 0));
  }

  useEffect(() => {
    if (formLibraryEditStatusSelector) {
      let usrId = currentUser.id;
      if (currentUser.linkUser)
        usrId = currentUser.linkUser;
      if (workflow?.oldWorkflowId > 0) {
        dispatch(loadAppFormLibrary(workflow.oldWorkflowId, usrId, { subscribe: false, forceRefetch: true }));
        dispatch(loadAppRuningTasks(workflow.oldWorkflowId, usrId, { subscribe: false, forceRefetch: true }));
      }
    }
  }, [formLibraryEditStatusSelector, workflow?.oldWorkflowId, currentUser?.id, currentUser?.linkUser, dispatch]);

  const handleDocumentLoad = () => {
    let usrId = currentUser.id;
    if (currentUser.linkUser)
      usrId = currentUser.linkUser;
    if (workflow?.oldWorkflowId > 0) {
      dispatch(loadAppFormLibrary(workflow.oldWorkflowId, usrId, { subscribe: false, forceRefetch: true }));
      dispatch(loadAppDocuments(workflow.oldWorkflowId, usrId, { subscribe: false, forceRefetch: true }));
    }
  }

  const achDocumentCount = sentDocs.filter((doc) => doc.docType?.startsWith("ach_")).length;
  const completedFormCount = appFormLibrary.filter((item) => item?.lastUpdate).length;

  const getPopover = (notes) => {
    const popover = (
      <Popover id="popover-basic">
        <Popover.Title as="h4">Notes</Popover.Title>
        <Popover.Content>{notes}</Popover.Content>
      </Popover>
    );
    return (
      <OverlayTrigger trigger="click" placement="top" overlay={popover}>
        <button
          type="button"
          style={ACH_STYLES.docNotesBtn}
          title="View Notes"
        >
          <i className="fa fa-sticky-note-o" aria-hidden="true"></i>
        </button>
      </OverlayTrigger>
    );
  };

  const getVoidCheck = (item) => {
    const achDocs = sentDocs.filter(
      (x) => x.docType === `ach_${item.id}`
    );

    if (achDocs.length === 0) {
      return <span style={{ color: "#94a3b8", fontSize: "13px" }}>-</span>;
    }

    return (
      <div style={{ display: "flex", flexDirection: "column", gap: "8px", alignItems: "flex-start" }}>
        {achDocs.map(doc => {
          const isPdf = doc.documentName?.toLowerCase().endsWith(".pdf");
          return (
            <div key={doc.id} style={ACH_STYLES.docCard}>
              <div style={ACH_STYLES.docHeaderRow}>
                <div style={ACH_STYLES.docTitleGroup}>
                  <span style={{
                    ...ACH_STYLES.docBadgeIcon }}>
                    <i className={`fa ${isPdf ? "fa-file-pdf-o" : "fa-file-text-o"}`} aria-hidden="true"></i>
                  </span>
                  <button
                    type="button"
                    style={ACH_STYLES.docNameBtn}
                    title={doc.documentName}
                    onClick={() => handleDownloadFile(doc)}
                  >
                    {doc.documentName || "Document"}
                  </button>
                </div>

                <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                  {doc.notes && getPopover(doc.notes)}
                  <button
                    type="button"
                    style={ACH_STYLES.docDeleteBtn}
                    title="Delete File"
                    onClick={() => handleDeleteAchDoc(doc.id)}
                  >
                    <i className="fa fa-trash" aria-hidden="true"></i>
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  return <React.Fragment>
    <div className="mt-2 form-group">
      <style>{`
        .ach-info-table tbody tr:hover {
          background-color: #fbfdff;
        }
      `}</style>

      <div style={ACH_STYLES.panel}>
        <div style={ACH_STYLES.cardHeader}>
          <div style={ACH_STYLES.headerRow}>
            <div style={ACH_STYLES.titleRow}>
              <span style={ACH_STYLES.titleIcon}>
                  <i className="fa fa-university" aria-hidden="true"></i>
                </span>
              <div>
                <h2 style={ACH_STYLES.panelTitle}>ACH Information</h2>
                <p style={ACH_STYLES.panelText}>
                  Click on the ACH Form link to complete and then click on the Save button to send the details.
                </p>
              </div>
            </div>
            <div>
              <button
                type="button"
                className="btn btn-sm"
                style={ACH_STYLES.refreshButton}
                onClick={handleDocumentLoad}
              >
                <i className="fa fa-refresh" aria-hidden="true"></i> Refresh
              </button>
            </div>
          </div>
        </div>

        <div style={ACH_STYLES.cardBody}>
          <div className="table-responsive" style={ACH_STYLES.tableWrap}>
            <table className="table align-items-center table-hover mb-0 ach-info-table" style={ACH_STYLES.table}>
              <thead>
                <tr>
                  <th scope="col" style={ACH_STYLES.tableHeadCell}>ACH Information</th>
                  <th scope="col" style={ACH_STYLES.tableHeadCell}>ACH Form</th>
                  <th scope="col" style={ACH_STYLES.tableHeadCell}>Void Check</th>
                  <th scope="col" style={{ ...ACH_STYLES.tableHeadCell, textAlign: "center" }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {appFormLibrary.map((item, index) => {
                  const bankName = wfTask && getFieldData(wfTask.stateData, "achinfo", "Bank_Name");
                  const routingNumber = wfTask && getFieldData(wfTask.stateData, "achinfo", "Routing");
                  const accountNumber = wfTask && getFieldData(wfTask.stateData, "achinfo", "Account_No");
                  const { allowEdit, lastUpdate, editRemarks } = item;

                  return (
                    <tr key={item.id || index}>
                      {/* COL 1: ACH INFORMATION (ENHANCED CARD CONTAINER) */}
                      <td className="text-wrap align-top" style={ACH_STYLES.cell}>
                        <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                          {/* TOP ROW: BANK NAME & STATUS PILL */}
                          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "12px" }}>
                            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                              
                              <div>
                                <div style={{ fontSize: "11px", fontWeight: 600, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.03em" }}>
                                  Bank Name
                                </div>
                                <div style={{ fontSize: "14px", fontWeight: 600, color: "#0f172a" }}>
                                  {bankName || "Citi Bank"}
                                </div>
                              </div>
                            </div>

                            <div>
                              {lastUpdate ? (
                                <span style={ACH_STYLES.completePill}>
                                  
                                  Complete
                                </span>
                              ) : (
                                <span style={ACH_STYLES.pendingPill}>
                                  
                                  Required
                                </span>
                              )}
                            </div>
                          </div>

                          {/* MINI GRID FOR ROUTING & ACCOUNT NUMBER */}
                          <div style={{
                            display: "grid",
                            gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))",
                            gap: "8px",
                            paddingTop: "10px",
                            borderTop: "1px dashed #e2e8f0"
                          }}>
                            {/* ROUTING CARD */}
                            <div style={{
                              background: "#ffffff",
                              border: "1px solid rgba(219, 234, 254, 0.9)",
                              borderRadius: "0px",
                              padding: "8px 12px",
                              boxShadow: "none",
                              display: "flex",
                              alignItems: "center",
                              gap: "10px"
                            }}>
                              
                              <div style={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
                                <span style={{ fontSize: "10.5px", fontWeight: 600, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.02em" }}>
                                  Routing Number
                                </span>
                                <span style={{
                                  fontFamily: "SFMono-Regular, Menlo, Monaco, Consolas, monospace",
                                  fontSize: "12.5px",
                                  fontWeight: 600,
                                  color: routingNumber ? "#0f172a" : "#94a3b8",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap"
                                }}>
                                  {routingNumber || "-"}
                                </span>
                              </div>
                            </div>

                            {/* ACCOUNT NUMBER CARD */}
                            <div style={{
                              background: "#ffffff",
                              border: "1px solid rgba(219, 234, 254, 0.9)",
                              borderRadius: "0px",
                              padding: "8px 12px",
                              boxShadow: "none",
                              display: "flex",
                              alignItems: "center",
                              gap: "10px"
                            }}>
                              
                              <div style={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
                                <span style={{ fontSize: "10.5px", fontWeight: 600, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.02em" }}>
                                  Account Number
                                </span>
                                <span style={{
                                  fontFamily: "SFMono-Regular, Menlo, Monaco, Consolas, monospace",
                                  fontSize: "12.5px",
                                  fontWeight: 600,
                                  color: accountNumber ? "#0f172a" : "#94a3b8",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap"
                                }}>
                                  {accountNumber || "-"}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </td>

                      {/* COL 2: ACH FORM (WITH REQUEST EDIT BUTTON & EDIT TIMESTAMP) */}
                      <td className="text-wrap align-top" style={ACH_STYLES.cell}>
                        <div style={{ display: "flex", flexDirection: "column", gap: "8px", alignItems: "flex-start" }}>
                          <button
                            type="button"
                            className="btn btn-sm text-left"
                            style={{
                              ...(allowEdit === 1 ? ACH_STYLES.primaryAction : ACH_STYLES.secondaryAction),
                              ...ACH_STYLES.formActionButton
                            }}
                            onClick={() => handleFormShowHide(item)}
                          >
                            
                            <span style={ACH_STYLES.formActionCopy}>
                              <span style={ACH_STYLES.formActionText}>
                                  <i className={`fa ${!lastUpdate ? "fa-exclamation-circle" : allowEdit === 1 ? "fa-pencil-square-o" : "fa-eye"}`} style={{ marginRight: "6px" }} aria-hidden="true"></i>
                                  {!lastUpdate ? <>Complete ACH <span className="text-danger">*</span></> : allowEdit === 1 ? "Edit ACH" : "View ACH"}
                              </span>
                              
                            </span>
                          </button>

                          {allowEdit === 0 && (
                            <button
                              type="button"
                              className="btn btn-sm"
                              style={ACH_STYLES.requestEditButton}
                              onClick={() => handleNotesComments("", item)}
                              title="Request For Edit"
                            >
                              <i className="fa fa-pencil-square-o" aria-hidden="true"></i>
                              Request For Edit
                            </button>
                          )}

                          {allowEdit === 2 && (
                            <div style={ACH_STYLES.mutedText}>
                              <span style={ACH_STYLES.statusPill}>Request Sent</span>
                              {editRemarks && (
                                <div className="mt-1">{editRemarks}</div>
                              )}
                            </div>
                          )}

                          {lastUpdate && (
                            <div style={ACH_STYLES.lastEditBadge}>
                              
                              <span>
                                <strong>Edited:</strong>{" "}
                                <Moment format="MMM Do YYYY, h:mm a">
                                  {lastUpdate}
                                </Moment>
                              </span>
                            </div>
                          )}
                        </div>
                      </td>

                      {/* COL 3: VOID CHECK (SHOWS ONLY UPLOADED DOCS) */}
                      <td className="align-top" style={ACH_STYLES.cell}>{getVoidCheck(item)}</td>

                      {/* COL 4: ACTION (UPLOAD BUTTON) */}
                      <td className="align-top text-center" style={ACH_STYLES.cell}>
                        <button
                          type="button"
                          className="btn btn-sm"
                          style={ACH_STYLES.primaryAction}
                          onClick={() => handleUploadDocModalShowHide(`ach_${item.id}`)}
                        >
                          <i className="fa fa-upload" aria-hidden="true"></i> Upload
                        </button>
                      </td>
                    </tr>
                  );
                })}
                {appFormLibrary.length === 0 && (
                  <tr>
                    <td colSpan="4" style={ACH_STYLES.emptyState}>
                      ACH information is not available yet.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>

          </div>
        </div>
      </div>
    </div>
    {docUploadShowHide &&
      <UploadDocPopup
        uploadDocShowHide={docUploadShowHide}
        handleUploadDocModalShowHide={handleUploadDocModalShowHide}
        runningApplication={reviewerTask ? [reviewerTask] : []}
        selectedDocType={selectedDocType}
        contactDoc="Your Voided Check has been successfully attached to workbench portal"
      ></UploadDocPopup>}

    {formShowHide && <ViewFormModal
      viewFormShowHide={formShowHide}
      handleViewForm={handleFormShowHide}
      workflowTask={wfTask}
      selectedForm={formObj}
      handleFormUpdate={handleFormUpdate}
      allowEdit={allowEdit}
      formName="ACH Information"
    ></ViewFormModal>}

    {/* {formShowHide && <ViewForm
      formShowHide={formShowHide}
      handleFormShowHide={handleFormShowHide}
      workflowTask={wfTask}
      selectedForm={formObj}
    ></ViewForm>} */}

    {noteShowHide && <AddEditNotes
      noteShowHide={noteShowHide}
      handleNoteShowHide={handleNotesComments}
      notesType={notesType}
      candidateObj={currentUser}
      selectedObj={formObj}
    ></AddEditNotes>}
  </React.Fragment>
}
